/**
 * Centralised sign-out helper for the post-Amplify SPA.
 *
 * Replaces the legacy `useAuthenticator().signOut()` callback that
 * `<SignOutButton>`, `<Layout>`, and the idle-timer in `<DefaultHome>`
 * used to call. The new flow:
 *
 *   1. Clear redux user/role state so the next render doesn't briefly
 *      show a stale `currentUser` while the browser is mid-navigation.
 *   2. Best-effort Amplify storage clear (`Auth.signOut()`). This does
 *      not throw when no Amplify session exists, so it's safe to call
 *      in the post-cutover flow where most users only have a
 *      simpl_auth cookie. Keeping the call means users who came in
 *      via the §6.7 cognito-exchange bridge don't leave Cognito
 *      tokens lingering in localStorage after they sign out.
 *   3. Redirect the browser to `simplAuthSignOutUrl()`. simpl_auth
 *      clears the `SIMPL_AUTH_JWT_<env>` cookie + AIOHTTP_SESSION on
 *      its end, then bounces the browser back to `returnTo`. After
 *      that bounce the SPA's session probe sees no cookie and the
 *      `<SimplAuthGuard>` falls through to `<SignInRedirect>`.
 *
 * Lives next to `simplAuthSession.ts` rather than in
 * `common-ui/SignOutButton.tsx` so the idle-timer + nav menu can
 * share it without dragging the button component into their import
 * graph.
 */

import { Auth } from "aws-amplify";
import { AnyAction, Dispatch } from "@reduxjs/toolkit";

import {
  setAdminAuthority,
  setCurrentUser,
  setUserRoles
} from "../Users/usersSlice";
import { setTrayOwner } from "../Users/bulkEndTraySlice";
import { simplAuthSignOutUrl } from "./simplAuthSession";
import { isCognitoAuthMode } from "./authMode";

type PerformSimplAuthSignOutOptions = {
  dispatch: Dispatch<AnyAction>;
  /**
   * URL to land on after simpl_auth completes sign-out. Defaults to
   * the SPA's origin. simpl_auth validates this against its
   * `*.simpl.org`/dev-localhost allow-list before honouring the
   * redirect, so a hijacked argument can't redirect off-platform.
   */
  returnTo?: string;
};

export async function performSimplAuthSignOut({
  dispatch,
  returnTo
}: PerformSimplAuthSignOutOptions): Promise<void> {
  dispatch(setCurrentUser(undefined));
  dispatch(setUserRoles([]));
  dispatch(setAdminAuthority(null));
  // SE-1723: drop the bulk-end tray on sign-out so it never carries into the
  // next account's session (it's also keyed by user id in localStorage).
  dispatch(setTrayOwner({ ownerId: null, entries: [] }));

  // Best-effort Amplify cleanup for users whose browser still has
  // Cognito material from the bridge era. Swallow errors — Amplify
  // throws "no current user" when there's no session and we don't
  // care about that path.
  try {
    await Auth.signOut();
  } catch {
    // expected for cookie-only sessions
  }

  // Cognito-mode envs (dev, testone, testtwo, testthree, sadev, hsdev) have no
  // simpl_auth to bounce through — the Auth.signOut() above already cleared the
  // Cognito session, so just return to the origin (or returnTo).
  if (isCognitoAuthMode()) {
    window.location.assign(
      returnTo ??
        (typeof window !== "undefined" ? window.location.origin : "/")
    );
    return;
  }

  window.location.assign(simplAuthSignOutUrl(returnTo));
}
